/**
 * CommunityLayout - Layout wrapper with sidebar + content area.
 *
 * Wraps community-specific pages with:
 *   - CommunitySidebar on the left (community navigation)
 *   - Content area on the right (page content via Outlet)
 *
 * Fetches community data via React Query and passes it to the sidebar.
 * Child routes receive community data via Outlet context.
 */

import React, { useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { Spinner, Alert } from 'react-bootstrap'
import { useCommunity } from '../hooks/useCommunities'
import CommunitySidebar from './CommunitySidebar'
import '../styles/community-layout.css'

function CommunityLayout() {
  const { id } = useParams()
  const { data: community, isLoading, isError, error } = useCommunity(id)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const handleToggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev)
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="community-layout">
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
          }}
        >
          <div className="text-center">
            <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
            <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
              Loading community...
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="community-layout">
        <div style={{ flex: 1, padding: 'var(--space-8)' }}>
          <Alert variant="danger">
            <Alert.Heading>Failed to load community</Alert.Heading>
            <p>{error?.message || 'An unknown error occurred.'}</p>
            <hr />
            <a href="/communities">Back to Communities</a>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="community-layout">
      <CommunitySidebar
        community={community}
        collapsed={sidebarCollapsed}
        onToggle={handleToggleSidebar}
      />

      <main className="community-content">
        <div className="community-content-inner">
          {/*
            Outlet context provides the community object to all child routes.
            Child pages access it via: const { community } = useOutletContext()
          */}
          <Outlet context={{ community }} />
        </div>
      </main>
    </div>
  )
}

export default CommunityLayout
